import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Building2, ChevronRight, Loader, AlertCircle } from 'lucide-react';

const PortalSelectClient = () => {
  const { team_id } = useParams();
  const navigate = useNavigate();
  
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const response = await api.get(`/portal/${team_id}/select-client`);
      
      if (response.data.clients) {
        setClients(response.data.clients);
        
        // If only one client, auto-select it
        if (response.data.clients.length === 1) {
          handleSelectClient(response.data.clients[0].id);
        }
      }
    } catch (err) {
      console.error('Fetch clients error:', err);
      setError(err.response?.data?.error || 'Failed to load clients. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectClient = async (client_id) => {
    try {
      // Store selected client
      await api.post(`/portal/${team_id}/select-client`, {
        client_id: client_id
      });
      
      // Navigate to portal dashboard
      navigate(`/portal/${team_id}/dashboard`);
    } catch (err) {
      console.error('Select client error:', err);
      setError(err.response?.data?.error || 'Failed to select client. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-block p-4 bg-white/10 rounded-2xl backdrop-blur-sm mb-4">
            <Building2 className="w-12 h-12 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Select Your Organization</h1>
          <p className="text-purple-200">Choose which organization you'd like to access</p>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {loading ? (
            <div className="text-center py-12">
              <Loader className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600">Loading your organizations...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
                <AlertCircle className="w-10 h-10 text-red-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Error Loading Organizations
              </h2>
              <p className="text-gray-600 mb-6">{error}</p>
              <button
                onClick={fetchClients}
                className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                <Building2 className="w-10 h-10 text-gray-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                No Organizations Found
              </h2>
              <p className="text-gray-600">
                You don't have access to any organizations yet. Please contact your administrator.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-gray-600 mb-4">
                You have access to {clients.length} organization{clients.length !== 1 ? 's' : ''}
              </p>
              
              {clients.map((client) => (
                <button
                  key={client.id}
                  onClick={() => handleSelectClient(client.id)}
                  className="w-full flex items-center justify-between p-4 border-2 border-gray-200 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-all duration-200 group"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                      {client.name?.charAt(0) || 'C'}
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-gray-900 group-hover:text-purple-600">
                        {client.name || 'Unnamed Organization'}
                      </h3>
                      {client.email && (
                        <p className="text-sm text-gray-600">{client.email}</p>
                      )}
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-purple-600 group-hover:translate-x-1 transition-all" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && (
          <div className="text-center mt-6">
            <button
              onClick={() => navigate(`/portal/${team_id}/logout`)}
              className="text-purple-200 hover:text-white text-sm"
            >
              Sign out
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalSelectClient;
